import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { accountService } from "@/services/accountService";
import type { AccountCreate, AccountUpdate } from "@/types/account";

export const ACCOUNT_QUERY_KEYS = {
  ALL: ["accounts"],
  DETAIL: (accountUuid: string) => [...ACCOUNT_QUERY_KEYS.ALL, accountUuid],
};

export function useAccounts() {
  const queryClient = useQueryClient();

  // Get all accounts
  const accountsQuery = useQuery({
    queryKey: ACCOUNT_QUERY_KEYS.ALL,
    queryFn: () => accountService.getAccounts(),
  });

  // Create account mutation
  const createMutation = useMutation({
    mutationFn: (data: AccountCreate) => accountService.createAccount(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.ALL });
    },
  });

  // Update account mutation
  const updateMutation = useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: AccountUpdate }) =>
      accountService.updateAccount(uuid, data),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.ALL }),
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.DETAIL(variables.uuid) }),
      ]);
    },
  });

  // Delete account mutation
  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => accountService.deleteAccount(uuid),
    onSuccess: async (_, uuid) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.ALL }),
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.DETAIL(uuid) }),
      ]);
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: (uuid: string) => accountService.testConnection(uuid),
    onSuccess: async (_, uuid) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.ALL }),
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.DETAIL(uuid) }),
      ]);
    },
  });

  // Test connection with credentials mutation (for new accounts)
  const testConnectionWithCredentialsMutation = useMutation({
    mutationFn: (credentials: AccountCreate) =>
      accountService.testConnectionWithCredentials(credentials),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.ALL });
    },
  });

  return {
    // Data
    accounts: accountsQuery.data,
    isLoading: accountsQuery.isLoading,
    isError: accountsQuery.isError,
    error: accountsQuery.error,
    refetch: accountsQuery.refetch,

    // Mutations
    createAccount: createMutation.mutate,
    createAccountAsync: createMutation.mutateAsync,

    updateAccount: updateMutation.mutate,
    updateAccountAsync: updateMutation.mutateAsync,

    deleteAccount: deleteMutation.mutate,
    deleteAccountAsync: deleteMutation.mutateAsync,

    testConnection: testConnectionMutation.mutate,
    testConnectionWithCredentials: testConnectionWithCredentialsMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isTesting: testConnectionMutation.isPending,
    isTestingWithCredentials: testConnectionWithCredentialsMutation.isPending,
  };
}

export function useAccount(accountUuid: string) {
  return useQuery({
    queryKey: ACCOUNT_QUERY_KEYS.DETAIL(accountUuid),
    queryFn: () => accountService.getAccount(accountUuid),
    enabled: !!accountUuid,
  });
}
